import { useEffect, useState } from 'react';
import MentorPortalNav from './MentorPortalNav';
import MentorProfileChrome from './MentorProfileChrome';
import MentorProfileHeader from './MentorProfileHeader';
import MentorProfileChecklist from './MentorProfileChecklist';
import MentorProfileDetails from './MentorProfileDetails';
import MentorProfileTracks from './MentorProfileTracks';
import MentorProfileNotifications from './MentorProfileNotifications';
import MentorPortalService, { MentorProfilePayload, MentorProfilePutBody } from './MentorPortalService';
import { EMPTY_MENTOR_COPY } from './mentorDashboardFormat';
import './mentorDashboard.css';
import './mentorProfile.css';

const emptyDraft = (): MentorProfilePutBody => ({
    bio: '',
    currentCompany: '',
    designation: '',
    location: '',
    linkedinUrl: '',
    specificAreas: '',
    preferredTrackIds: [],
    notifyNewMessage: true,
    notifyBooking: true,
    notifySessionReminder: true,
    notifyMonthlyDigest: true,
});

const asText = (value: string | null | undefined): string => (value == null ? '' : String(value));

export const draftFromPayload = (payload: MentorProfilePayload): MentorProfilePutBody => ({
    bio: asText(payload.bio),
    currentCompany: asText(payload.currentCompany),
    designation: asText(payload.designation),
    location: asText(payload.location),
    linkedinUrl: asText(payload.linkedinUrl),
    specificAreas: asText(payload.specificAreas),
    preferredTrackIds: [...(payload.preferredTrackIds || [])],
    notifyNewMessage: payload.notifyNewMessage !== false,
    notifyBooking: payload.notifyBooking !== false,
    notifySessionReminder: payload.notifySessionReminder !== false,
    notifyMonthlyDigest: payload.notifyMonthlyDigest !== false,
});

const sortedIds = (ids: number[]): number[] => [...ids].sort((left, right) => left - right);

export const draftsEqual = (left: MentorProfilePutBody, right: MentorProfilePutBody): boolean =>
    left.bio === right.bio &&
    left.currentCompany === right.currentCompany &&
    left.designation === right.designation &&
    left.location === right.location &&
    left.linkedinUrl === right.linkedinUrl &&
    left.specificAreas === right.specificAreas &&
    left.notifyNewMessage === right.notifyNewMessage &&
    left.notifyBooking === right.notifyBooking &&
    left.notifySessionReminder === right.notifySessionReminder &&
    left.notifyMonthlyDigest === right.notifyMonthlyDigest &&
    sortedIds(left.preferredTrackIds).join(',') === sortedIds(right.preferredTrackIds).join(',');

/** Client guard aligned with parseMentorProfileLinkedinUrl (required http/https URL). */
export const validateMentorProfileDraft = (draft: MentorProfilePutBody): string | null => {
    const linkedin = draft.linkedinUrl == null ? '' : String(draft.linkedinUrl).trim();
    if (!linkedin) {
        return 'LinkedIn URL is required.';
    }
    try {
        const parsed = new URL(linkedin);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            return 'LinkedIn URL must be a valid URL.';
        }
        if (!parsed.hostname) {
            return 'LinkedIn URL must be a valid URL.';
        }
    } catch {
        return 'LinkedIn URL must be a valid URL.';
    }
    return null;
};

const MentorProfile = () => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [forbidden, setForbidden] = useState(false);
    const [payload, setPayload] = useState<MentorProfilePayload | null>(null);
    const [saved, setSaved] = useState<MentorProfilePutBody>(emptyDraft());
    const [draft, setDraft] = useState<MentorProfilePutBody>(emptyDraft());
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const applyPayload = (next: MentorProfilePayload) => {
        const nextDraft = draftFromPayload(next);
        setPayload(next);
        setSaved(nextDraft);
        setDraft(nextDraft);
        setSaveError(null);
    };

    useEffect(() => {
        let cancelled = false;
        MentorPortalService.getProfile()
            .then((data) => {
                if (cancelled) return;
                applyPayload(data);
                setLoadError(null);
                setForbidden(false);
            })
            .catch((err: { status?: number; message?: string }) => {
                if (cancelled) return;
                if (err?.status === 403 || err?.status === 401) {
                    setForbidden(true);
                    setLoadError(null);
                    return;
                }
                setLoadError(err?.message || 'Unable to load mentorship data.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const onChange = (patch: Partial<MentorProfilePutBody>) => {
        setDraft((current) => ({ ...current, ...patch }));
        setSaveError(null);
    };

    const onSave = () => {
        const validationError = validateMentorProfileDraft(draft);
        if (validationError) {
            setSaveError(validationError);
            return;
        }
        setIsSaving(true);
        setSaveError(null);
        MentorPortalService.putProfile(draft)
            .then((stored) => {
                applyPayload(stored);
            })
            .catch((err: unknown) => {
                setSaveError(MentorPortalService.errorMessage(err));
            })
            .finally(() => setIsSaving(false));
    };

    const onDiscard = () => {
        setDraft(saved);
        setSaveError(null);
    };

    const dirty = !draftsEqual(draft, saved);
    const isEmptyForbidden = !loading && !loadError && forbidden;

    return (
        <div className="container py-4 mdash mprof">
            
            <MentorPortalNav />
            <MentorProfileChrome />
            {loading && <p className="mdash-empty">Loading…</p>}
            {!loading && loadError && <p className="mdash-empty">{loadError}</p>}
            {isEmptyForbidden && <p className="mdash-empty">{EMPTY_MENTOR_COPY}</p>}
            {!loading && !loadError && !forbidden && payload ? (
                <>
                    <MentorProfileHeader
                        dirty={dirty}
                        isSaving={isSaving}
                        saveError={saveError}
                        onSave={onSave}
                        onDiscard={onDiscard}
                    />
                    

                    <div className='row g-3 mb-3'>
                        <MentorProfileChecklist checklist={payload.checklist} />
                        <MentorProfileDetails identity={payload.identity} draft={draft} onChange={onChange} />
                        <MentorProfileTracks
                            catalog={payload.trackCatalog || []}
                            draft={draft}
                            matchingHelper={payload.matchingHelper}
                            onChange={onChange}
                        />
                    </div>
                    
                    <MentorProfileNotifications draft={draft} onChange={onChange} />
                </>
            ) : null}
        </div>
    );
};

export default MentorProfile;
